import React, { useEffect, useState } from "react";
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import Check from "@material-ui/icons/Check";
import DeleteIcon from "@material-ui/icons/Delete";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

/*
  BCM-881 — CMO Inventory Mapping.

  One row per status or column of a CMO's inventory file, held against that
  CMO in the Configurations step. It is what decides, for that CMO:

    · which status columns the CMO Inventory Status dashboard shows, and under
      what names (the rows where Selection is on, under Renamed Status/Column)
    · which quantities make up CMO total qty. and CMO Unrestricted Qty.
    · the tooltip text shown against those two column headers, which the
      system generates from this mapping rather than anyone typing it

  Status Mapping applies only where Category is Column — a column carries no
  status of its own, so the status its quantity belongs to is picked here. It
  is NA for a row whose Category is Status. The statuses offered are those
  switched on in the Inventory Status tab of Sponsor Network Configuration.

  The component owns no data: it renders `value` and reports every change
  through `onChange`, so the screen embedding it saves the rows in its own
  payload. fetchInventoryStatuses is the Sponsor Network endpoint and is not
  delivered yet; until it is, Status Mapping offers whatever statuses the rows
  themselves already name, so the table still works.
*/

const CATEGORIES = ["Status", "Column"];
// The UI reads Yes/No; the API takes booleans.
const YES_NO = [
  { id: true, name: "Yes" },
  { id: false, name: "No" },
];

export const emptyMappingRow = () => ({
  // The row IS the API shape, so updateVendorPlantDetails can take it as it
  // stands. inventoryMappingID 0 marks a row that has never been saved.
  inventoryMappingID: 0,
  category: "Status",
  statusInFile: "",
  columnNameInFile: "",
  renamedColumnName: "",
  mappedStatusID: null,
  partOfCMOTotalQty: false,
  partOfCMOUnrestrictedQty: false,
  markForDelete: false,
  // Selection comes up switched on, as it does for a new row in the Inventory
  // Status tab. A row is mapped so that it is displayed -- "the statuses and
  // columns displayed for a CMO ... are the rows where Selection is switched
  // on" -- so a row added and saved untouched would otherwise be stored and
  // silently show nothing. Switching it off stays a deliberate act.
  activated: true,
});

/*
  The iOS-style toggle used by every other Selection switch in CMO
  Configurations (Product Type, Document Type, Plant tabs). Each of those files
  carries its own copy, so this one does too, and the switch looks the same.
*/
const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    disabled={props.disabled}
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

/*
  Fixed column widths, so every column stays in the same place in view and in
  edit. Without them the browser sizes the columns from their content, and in
  view the Action column is empty and the controls are disabled, so the
  columns shifted between the two.
*/
const COLUMNS = [
  { title: "Selection", width: 100 },
  { title: "Category", width: 150 },
  { title: "Status/Column Name", width: 200 },
  { title: "Renamed Status/Column", width: 200 },
  { title: "Status Mapping", width: 170 },
  { title: "Part of CMO Total Qty", width: 150 },
  { title: "Part of CMO Unrestricted Qty", width: 150 },
  { title: "Action", width: 80 },
];

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 600,
    borderBottom: "2px solid #000",
    display: "inline-block",
    paddingBottom: "4px",
    marginBottom: theme.spacing(2),
  },
  // Cell borders, header colour and padding as the Product Type Selection table.
  table: {
    tableLayout: "fixed",
    "& td, & th": {
      borderRight: "1px solid #e0e0e0",
    },
    "& td:last-child, & th:last-child": {
      borderRight: "none",
    },
  },
  headerCell: {
    fontWeight: 600,
    backgroundColor: "#f9f9fc",
    padding: "12px 16px",
    lineHeight: 1.3,
  },
  bodyCell: {
    padding: "8px 16px",
    verticalAlign: "middle",
  },
  control: {
    width: "100%",
    "& input": { padding: "8px 10px" },
  },
  iosSwitch: {
    "& > *": {
      height: "22px !important",
    },
    "& .MuiSwitch-switchBase .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "& .MuiSwitch-switchBase": {
      left: "2px",
      top: "-1px",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      left: "auto",
      right: "18px",
    },
    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track": {
      backgroundColor: "#1890ff !important",
    },
  },
  checkedSwitch: {
    position: "absolute",
    width: "16px !important",
    left: "12px",
    top: "-3px",
    color: "white",
  },
  deleteIcon: { color: "#ec6535" },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  notApplicable: { color: "#bdbdbd" },
}));

const CmoInventoryMapping = (props) => {
  // showTitle is off where the page gives the table its own section header.
  const { value, onChange, readOnly = false, sponsorId, showTitle = true } = props;
  const classes = useStyles();
  const rows = Array.isArray(value) ? value : [];
  // Rows flagged for deletion stay in the payload but leave the table.
  const visibleRows = rows.filter((row) => !row?.markForDelete);

  const [inventoryStatuses, setInventoryStatuses] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchInventoryStatuses`;
        // cmoConfigBool true narrows the list to the activated statuses, which
        // is what Status Mapping is allowed to offer.
        const response = await axios.post(url, { sponsorId, cmoConfigBool: true });
        const list = (
          response?.data?.data?.inventoryStatuses ||
          response?.data?.inventoryStatuses ||
          []
        )
          .filter((item) => item && item.statusID !== undefined && item.statusName)
          // Status Mapping stores mappedStatusID, so the option has to carry
          // the id; the name is only what the user reads.
          .map((item) => ({ id: item.statusID, name: item.statusName }));
        if (!cancelled) setInventoryStatuses(list);
      } catch (err) {
        // Not delivered yet — Status Mapping falls back to the statuses the
        // rows themselves name, below.
        console.error("fetchInventoryStatuses:", err);
        if (!cancelled) setInventoryStatuses([]);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [sponsorId]);

  // Each option is { id, name }: the row stores mappedStatusID, the user reads
  // the name. Until the Sponsor Network endpoint exists there is nothing to
  // offer, so the dropdown is simply empty rather than offering names that
  // carry no id to save.
  const statusOptions = inventoryStatuses;

  const update = (index, field, fieldValue) => {
    const next = rows.map((row, i) => {
      if (i !== index) return row;
      const updated = { ...row, [field]: fieldValue };
      /*
        Status Mapping applies only to a Column row, and the file name lives in
        a different field per category, so switching category clears whichever
        no longer applies.
      */
      if (field === "category") {
        if (fieldValue === "Status") {
          updated.mappedStatusID = null;
          updated.columnNameInFile = "";
        } else {
          updated.statusInFile = "";
        }
      }
      return updated;
    });
    onChange(next);
  };

  const addRow = () => onChange([...rows, emptyMappingRow()]);
  /*
    A row that has never been saved is simply dropped. One that exists in the
    vault has to travel to the API as markForDelete so Corda can retire it, so
    it is flagged and hidden rather than removed from the payload.
  */
  const removeRow = (index) => {
    const row = rows[index];
    if (!row?.inventoryMappingID) {
      onChange(rows.filter((_, i) => i !== index));
      return;
    }
    onChange(
      rows.map((item, i) => (i === index ? { ...item, markForDelete: true } : item))
    );
  };

  /*
    Options are either plain strings (Category, the Yes/No columns) or
    { id, name } pairs (Status Mapping, which stores the id).
  */
  const selectCell = (index, field, options, disabled = false) => (
    <Select
      value={rows[index][field] ?? ""}
      onChange={(e) => update(index, field, e.target.value)}
      className={classes.control}
      variant="outlined"
      displayEmpty
      disabled={readOnly || disabled}
    >
      <MenuItem value="">
        <span style={{ color: "#9e9e9e" }}>Select</span>
      </MenuItem>
      {options.map((option) => {
        const value = typeof option === "object" ? option.id : option;
        const label = typeof option === "object" ? option.name : option;
        return (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        );
      })}
    </Select>
  );

  const textCell = (index, field, placeholder) => (
    <TextField
      value={rows[index][field] || ""}
      onChange={(e) => update(index, field, e.target.value)}
      onBlur={(e) => {
        const text = e.target.value;
        if (typeof text === "string" && text.trim() !== text) {
          update(index, field, text.trim());
        }
      }}
      className={classes.control}
      variant="outlined"
      placeholder={placeholder}
      disabled={readOnly}
    />
  );

  return (
    <div style={{ marginTop: showTitle ? 32 : 0, marginBottom: 32 }}>
      {showTitle && (
        <Typography variant="h6" className={classes.title}>
          CMO Inventory Mapping
        </Typography>
      )}

      <TableContainer component={Paper} style={{ maxWidth: 1300, overflowX: "auto" }}>
        <Table className={classes.table}>
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.title} style={{ width: column.width }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column.title} className={classes.headerCell}>
                  {column.title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ padding: "24px" }}>
                  <Typography variant="body2" color="textSecondary">
                    No statuses or columns mapped for this CMO yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => {
                const index = rows.indexOf(row);
                return (
                <TableRow key={row.inventoryMappingID || `new-${index}`}>
                  <TableCell className={classes.bodyCell}>
                    <span style={{ position: "relative" }}>
                      <IOSSwitch
                        className={classes.iosSwitch}
                        color="primary"
                        checked={!!row.activated}
                        onChange={(e) => update(index, "activated", e.target.checked)}
                        disabled={readOnly}
                      />
                      {!!row.activated && <Check className={classes.checkedSwitch} />}
                    </span>
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {selectCell(index, "category", CATEGORIES)}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {/* The status or column as the CMO's own inventory file
                        names it. */}
      {/* One input, two API fields: a Status row names a status in the
                        file, a Column row names a column. */}
                    {textCell(
                      index,
                      row.category === "Column" ? "columnNameInFile" : "statusInFile",
                      "As named in the file"
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {/* What the inventory dashboards display instead. */}
                    {textCell(index, "renamedColumnName", "Displayed name")}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {row.category === "Column" ? (
                      selectCell(index, "mappedStatusID", statusOptions)
                    ) : (
                      <span className={classes.notApplicable}>NA</span>
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {selectCell(index, "partOfCMOTotalQty", YES_NO)}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {selectCell(index, "partOfCMOUnrestrictedQty", YES_NO)}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {!readOnly && (
                      <IconButton aria-label="remove row" onClick={() => removeRow(index)}>
                        <DeleteIcon className={classes.deleteIcon} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!readOnly && (
        <Button variant="outlined" onClick={addRow} className={classes.addButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Row
        </Button>
      )}
    </div>
  );
};

export default CmoInventoryMapping;
